"use strict";
/**
 * Arrow buttons for one of the landing page's screenshot strips.
 *
 * Each device's screenshots sit in a horizontal, scroll-snapping strip (see the
 * comment on the panels in index.html). The strip needs no script: a trackpad,
 * a finger, or the arrow keys on the focused strip all scroll it. What it lacks
 * is a way in for a mouse with only a vertical wheel, which is what these two
 * buttons are — so they are behaviour layered on top, and the CSS keeps them
 * hidden until this component marks the strip `.screenshots-strip-ready`.
 *
 * A button scrolls by most of a strip's width and lets `scroll-snap` pick the
 * resting point, so it always lands on a screenshot's edge at every breakpoint
 * without this file knowing how wide a slot is. Each button is disabled at its
 * end of the strip, and both hide when everything already fits (a future
 * device with two screenshots on a wide screen).
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.ScreenshotStrip = void 0;
const READY_CLASS = 'screenshots-strip-ready';
// How much of the visible width one click travels. Less than a full page so
// the screenshot that was peeking in at the edge stays on screen as an anchor.
const PAGE_FRACTION = 0.8;
class ScreenshotStrip {
    track;
    previous;
    next;
    /**
     * Wires one strip. Missing pieces leave it as authored — still scrollable,
     * just without buttons — rather than a broken section.
     */
    static attach(strip) {
        const track = strip.querySelector('.screenshots-strip-track');
        const previous = strip.querySelector('[data-strip-direction="-1"]');
        const next = strip.querySelector('[data-strip-direction="1"]');
        if (track && previous && next)
            new ScreenshotStrip(strip, track, previous, next);
    }
    constructor(strip, track, previous, next) {
        this.track = track;
        this.previous = previous;
        this.next = next;
        previous.addEventListener('click', () => this.page(-1));
        next.addEventListener('click', () => this.page(1));
        track.addEventListener('scroll', () => this.update(), { passive: true });
        // A panel starts `display: none` unless it is the default device, which
        // makes every measurement zero until it is shown. The observer fires when
        // the picker reveals it and on every resize, so the buttons are always
        // judged against the width the visitor actually sees.
        new ResizeObserver(() => this.update()).observe(track);
        strip.classList.add(READY_CLASS);
        this.update();
    }
    page(direction) {
        // `smooth` is a request, not a promise: prefers-reduced-motion users get
        // an instant jump where the browser honours that setting.
        this.track.scrollBy({ left: direction * this.track.clientWidth * PAGE_FRACTION, behavior: 'smooth' });
    }
    update() {
        const { scrollLeft, scrollWidth, clientWidth } = this.track;
        // One pixel of slack: fractional widths leave scrollLeft a hair short of
        // the maximum at the very end, which would keep "next" enabled forever.
        const overflows = scrollWidth - clientWidth > 1;
        this.previous.hidden = !overflows;
        this.next.hidden = !overflows;
        this.previous.disabled = scrollLeft <= 1;
        this.next.disabled = scrollLeft + clientWidth >= scrollWidth - 1;
    }
}
exports.ScreenshotStrip = ScreenshotStrip;
